import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useOnboardingStatus } from "../hooks/useOnboardingStatus";
import PsaSetupScreen, { PsaOnboardingSelections } from "./PsaSetupScreen";
import ITGlueSetupForm from "./ITGlueSetupForm";
import HuduSetupForm from "./HuduSetupForm";
import DattoSetupForm from "./DattoSetupForm";
import NableSetupForm from "./NableSetupForm";
import NinjaOneSetupForm from "./NinjaOneSetupForm";
import { getConnectorLogo } from "../utils/connectorUtils";
import { onboardingApi } from "../apis/onboardingApi";
import TeamsBotOnboarding from "./TeamsBotOnboarding";

const steps = ["PSA Configuration", "Documentation", "RMM", "Teams", "Complete"];

interface OnboardingWizardProps {
  mspId: number;
  onComplete: () => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ mspId, onComplete }) => {
  const { status, loading, updateProgress } = useOnboardingStatus(mspId);
  const [activeStep, setActiveStep] = useState(0);
  const [rmmChoice, setRmmChoice] = useState<"nable" | "ninjaone" | "datto" | null>(null);
  const [showDocsForm, setShowDocsForm] = useState<"itglue" | "hudu" | null>(null);

  // Only set initial step on mount based on backend status
  useEffect(() => {
    if (status && activeStep === 0) {
      // Determine initial step based on completion status
      if (!status.psaCompleted) {
        setActiveStep(0);
      } else if (!status.documentationCompleted) {
        setActiveStep(1);
      } else if (!status.rmmCompleted) {
        setActiveStep(2);
      } else if (!status.onboardingCompleted) {
        setActiveStep(3);
      } else {
        setActiveStep(4);
      }
    }
  }, [status, activeStep]);

  const handlePsaComplete = async (selections?: PsaOnboardingSelections) => {
    // Default: every PSA customer becomes a Theo customer, unless a subset
    // was picked in advanced settings. Fire-and-forget so onboarding never
    // blocks on the import; failures surface on Accounts.
    try {
      await onboardingApi.importAllCustomers(mspId, selections?.customerPsaIds);
    } catch (error) {
      console.error("Failed to auto-import PSA customers:", error);
    }
    setActiveStep(1);
    await updateProgress(2, false);
  };

  const handleDocumentationComplete = async () => {
    setActiveStep(2);
    await updateProgress(3, false);
  };

  const handleDocumentationSkip = async () => {
    setActiveStep(2);
    await updateProgress(3, false);
  };

  const handleRmmComplete = async () => {
    setActiveStep(3);
    await updateProgress(4, false);
  };

  const handleRmmSkip = async () => {
    setActiveStep(3);
    await updateProgress(4, false);
  };

  const finishOnboarding = async () => {
    setActiveStep(4);
    await updateProgress(5, true);
    setTimeout(() => {
      onComplete();
    }, 1000);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Typography>Loading onboarding...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#fafafa",
        py: 4,
        px: 3,
      }}
    >
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 700,
            mb: 4,
            color: "#1a1a1a",
            textAlign: "center",
          }}
        >
          Welcome to Theo
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            border: "1px solid #e5e5e5",
          }}
        >
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontFamily: "var(--theo-font-sans)",
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: "1px solid #e5e5e5",
          }}
        >
          {activeStep === 0 && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                }}
              >
                Step 1: Configure Your PSA
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  mb: 3,
                }}
              >
                Connect your PSA platform to start managing tickets with Theo. This step is required.
              </Typography>
              <PsaSetupScreen onSetupComplete={handlePsaComplete} />
            </Box>
          )}

          {activeStep === 1 && showDocsForm === null && (
            <Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setActiveStep(0)}
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  textTransform: "none",
                  mb: 3,
                  "&:hover": {
                    background: "#f5f5f5",
                  },
                }}
              >
                Back to PSA Configuration
              </Button>
              
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                }}
              >
                Step 2: Configure Documentation (Optional)
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  mb: 3,
                }}
              >
                Connect a documentation platform to enable documentation features. You can skip this step and configure it later.
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
                <Card
                  sx={{
                    border: "2px solid #e5e5e5",
                    borderRadius: 2,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#705d4a",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, display: "flex", alignItems: "center", gap: 3 }}>
                    <Box
                      component="img"
                      src={getConnectorLogo("ITGlue")}
                      alt="IT Glue"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: 600,
                          mb: 1,
                          color: "#1a1a1a",
                        }}
                      >
                        IT Glue
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          color: "#6c6c6c",
                        }}
                      >
                        Connect your IT Glue documentation platform
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowDocsForm("itglue")}
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        background: "#705d4a",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 500,
                        "&:hover": {
                          background: "#604d3a",
                        },
                      }}
                    >
                      Configure IT Glue
                    </Button>
                  </CardActions>
                </Card>

                <Card
                  sx={{
                    border: "2px solid #e5e5e5",
                    borderRadius: 2,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#705d4a",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, display: "flex", alignItems: "center", gap: 3 }}>
                    <Box
                      component="img"
                      src={getConnectorLogo("Hudu")}
                      alt="Hudu"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: 600,
                          mb: 1,
                          color: "#1a1a1a",
                        }}
                      >
                        Hudu
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          color: "#6c6c6c",
                        }}
                      >
                        Connect your Hudu documentation platform
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowDocsForm("hudu")}
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        background: "#705d4a",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 500,
                        "&:hover": {
                          background: "#604d3a",
                        },
                      }}
                    >
                      Configure Hudu
                    </Button>
                  </CardActions>
                </Card>
              </Box>

              <Button
                variant="text"
                onClick={handleDocumentationSkip}
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  textTransform: "none",
                  fontWeight: 500,
                  color: "#6c6c6c",
                  "&:hover": {
                    background: "#f5f5f5",
                  },
                }}
              >
                Skip for now
              </Button>
            </Box>
          )}

          {activeStep === 1 && showDocsForm === "itglue" && (
            <ITGlueSetupForm
              onBack={() => setShowDocsForm(null)}
              onSuccess={handleDocumentationComplete}
              onSkip={handleDocumentationSkip}
            />
          )}

          {activeStep === 1 && showDocsForm === "hudu" && (
            <HuduSetupForm
              mspId={mspId}
              onBack={() => setShowDocsForm(null)}
              onSuccess={handleDocumentationComplete}
              onSkip={handleDocumentationSkip}
            />
          )}

          {activeStep === 2 && !rmmChoice && (
            <Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setShowDocsForm(null);
                  setActiveStep(1);
                }}
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  textTransform: "none",
                  mb: 3,
                  "&:hover": {
                    background: "#f5f5f5",
                  },
                }}
              >
                Back to Documentation
              </Button>

              <Typography
                variant="h5"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                }}
              >
                Step 3: Configure RMM (Optional)
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  mb: 3,
                }}
              >
                Connect your RMM platform for enhanced device management. You can skip this step and configure it later.
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
                <Card
                  sx={{
                    border: "2px solid #e5e5e5",
                    borderRadius: 2,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#705d4a",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <Box
                      component="img"
                      src={getConnectorLogo("nable")}
                      alt="N-able"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        mx: "auto",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontWeight: 600,
                        mb: 1,
                        color: "#1a1a1a",
                      }}
                    >
                      N-able
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        color: "#6c6c6c",
                        mb: 2,
                      }}
                    >
                      Connect your N-able RMM platform
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0, justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      onClick={() => setRmmChoice("nable")}
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        background: "#705d4a",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 500,
                        "&:hover": {
                          background: "#604d3a",
                        },
                      }}
                    >
                      Configure N-able
                    </Button>
                  </CardActions>
                </Card>

                <Card
                  sx={{
                    border: "2px solid #e5e5e5",
                    borderRadius: 2,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#705d4a",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <Box
                      component="img"
                      src={getConnectorLogo("ninjaone")}
                      alt="NinjaOne"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        mx: "auto",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontWeight: 600,
                        mb: 1,
                        color: "#1a1a1a",
                      }}
                    >
                      NinjaOne
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        color: "#6c6c6c",
                        mb: 2,
                      }}
                    >
                      Connect your NinjaOne RMM platform
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0, justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      onClick={() => setRmmChoice("ninjaone")}
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        background: "#705d4a",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 500,
                        "&:hover": {
                          background: "#604d3a",
                        },
                      }}
                    >
                      Configure NinjaOne
                    </Button>
                  </CardActions>
                </Card>

                <Card
                  sx={{
                    border: "2px solid #e5e5e5",
                    borderRadius: 2,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#705d4a",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <Box
                      component="img"
                      src={getConnectorLogo("datto")}
                      alt="Datto RMM"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        mx: "auto",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontWeight: 600,
                        mb: 1,
                        color: "#1a1a1a",
                      }}
                    >
                      Datto RMM
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        color: "#6c6c6c",
                        mb: 2,
                      }}
                    >
                      Connect your Datto RMM platform
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0, justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      onClick={() => setRmmChoice("datto")}
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        background: "#705d4a",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 500,
                        "&:hover": {
                          background: "#604d3a",
                        },
                      }}
                    >
                      Configure Datto RMM
                    </Button>
                  </CardActions>
                </Card>
              </Box>

              <Button
                variant="text"
                onClick={handleRmmSkip}
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  textTransform: "none",
                  fontWeight: 500,
                  color: "#6c6c6c",
                  "&:hover": {
                    background: "#f5f5f5",
                  },
                }}
              >
                Skip for now
              </Button>
            </Box>
          )}

          {activeStep === 2 && rmmChoice === "nable" && (
            <NableSetupForm
              onBack={() => setRmmChoice(null)}
              onSuccess={handleRmmComplete}
              onSkip={handleRmmSkip}
            />
          )}

          {activeStep === 2 && rmmChoice === "ninjaone" && (
            <NinjaOneSetupForm
              onBack={() => setRmmChoice(null)}
              onSuccess={handleRmmComplete}
              onSkip={handleRmmSkip}
            />
          )}

          {activeStep === 2 && rmmChoice === "datto" && (
            <DattoSetupForm
              onBack={() => setRmmChoice(null)}
              onSuccess={handleRmmComplete}
              onSkip={handleRmmSkip}
            />
          )}

          {activeStep === 3 && (
            <Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setRmmChoice(null);
                  setActiveStep(2);
                }}
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  textTransform: "none",
                  mb: 3,
                  "&:hover": {
                    background: "#f5f5f5",
                  },
                }}
              >
                Back to RMM
              </Button>

              <Typography
                variant="h5"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                }}
              >
                Step 4: Install Theo in Microsoft Teams (Optional)
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  mb: 1,
                }}
              >
                Install Theo as an app in your Microsoft 365 tenant, then have each Theo User add it and send one message so their account is connected. You can skip this and finish it later from Integrations.
              </Typography>

              <TeamsBotOnboarding />

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={finishOnboarding}
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    background: "#705d4a",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      background: "#604d3a",
                    },
                  }}
                >
                  Continue
                </Button>
                <Button
                  variant="text"
                  onClick={finishOnboarding}
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    textTransform: "none",
                    fontWeight: 500,
                    color: "#6c6c6c",
                    "&:hover": {
                      background: "#f5f5f5",
                    },
                  }}
                >
                  Skip for now
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 4 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                }}
              >
                Setup Complete!
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  color: "#6c6c6c",
                  mb: 3,
                }}
              >
                Your Theo workspace is ready. Redirecting to dashboard...
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
